import { useSyncExternalStore } from "react";

export type Theme = "light" | "dark" | "system";

const KEY = "hrsys.theme";
const listeners = new Set<() => void>();

function stored(): Theme {
  try {
    const value = localStorage.getItem(KEY);
    return value === "dark" || value === "system" ? value : "light";
  } catch {
    return "light";
  }
}

function systemIsDark(): boolean {
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: dark)").matches;
}

/**
 * Puts the theme on the document.
 *
 * One attribute on the root element, which is all the stylesheet needs: the
 * dark theme is the same tokens with different values, so nothing in a
 * component knows or cares which is on. Light is the default and stays the
 * default for somebody who has never chosen — a counter under shop lighting
 * is not where a dark screen earns its place — so "system" is something a
 * person picks, not something that happens to them.
 */
export function applyTheme(theme: Theme = stored()) {
  const dark = theme === "dark" || (theme === "system" && systemIsDark());
  const root = document.documentElement;
  root.dataset.theme = dark ? "dark" : "light";
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", dark ? "#131519" : "#e9ebf0");
}

export function setTheme(theme: Theme) {
  try {
    localStorage.setItem(KEY, theme);
  } catch {
    // It lasts until the page is closed.
  }
  applyTheme(theme);
  for (const notify of listeners) notify();
}

/** The theme somebody chose, and the way to choose another. */
export function useTheme(): [Theme, (theme: Theme) => void] {
  const theme = useSyncExternalStore(
    (notify) => {
      listeners.add(notify);
      return () => listeners.delete(notify);
    },
    stored,
    () => "light" as Theme,
  );
  return [theme, setTheme];
}

/** Call once at start-up: applies the choice, and follows the system if asked to. */
export function startTheme() {
  applyTheme();
  window
    .matchMedia?.("(prefers-color-scheme: dark)")
    .addEventListener("change", () => applyTheme());
}
